/*
 * GasApp store landing page motion: word-by-word headlines, reveal on
 * scroll, the money-burned demo counter and gauge, the scroll story's steps,
 * and card tilt. The 3D reader is in gasapp-reader-3d.js.
 */
(() => {
  const root = document.querySelector('.ga-landing');
  if (!root) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  root.classList.add('ga-js');

  /* Headlines: each word slides up in turn. */
  root.querySelectorAll('[data-split]').forEach((el) => {
    let i = 0;
    const split = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.append(part);
              return;
            }
            const outer = document.createElement('span');
            outer.className = 'ga-word';
            const inner = document.createElement('span');
            inner.textContent = part;
            inner.style.setProperty('--i', i++);
            outer.append(inner);
            frag.append(outer);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          split(child);
        }
      });
    };
    split(el);
  });

  /* Reveal on scroll. */
  const revealer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        revealer.unobserve(entry.target);
      });
    },
    { threshold: 0.18, rootMargin: '0px 0px -6% 0px' },
  );
  root.querySelectorAll('[data-reveal], [data-split]').forEach((el) => revealer.observe(el));

  /* Demo counter: money burned on a drive, with a gas-pump style tenth of a cent. */
  root.querySelectorAll('[data-counter]').forEach((el) => {
    const dollarsEl = el.querySelector('[data-dollars]');
    const tenthEl = el.querySelector('[data-tenth]');
    const rateEl = root.querySelector('[data-rate]');
    const gauge = root.querySelector('[data-gauge]');
    const needle = gauge?.querySelector('[data-needle]');
    const arc = gauge?.querySelector('[data-arc]');
    const arcLength = arc ? arc.getTotalLength() : 0;
    if (arc) arc.style.strokeDasharray = `${arcLength} ${arcLength}`;

    const pricePerGallon = Number(el.dataset.price || 3.49);
    let spent = Number(el.dataset.start || 0);
    let running = false;
    let last = 0;
    let time = 0;
    let shownRate = '';

    const draw = (gallonsPerHour) => {
      const tenths = Math.floor(spent * 1000);
      dollarsEl.textContent = `$${Math.floor(tenths / 1000)}.${String(Math.floor(tenths / 10) % 100).padStart(2, '0')}`;
      tenthEl.textContent = String(tenths % 10);
      const centsPerSecond = ((gallonsPerHour * pricePerGallon) / 3600) * 100;
      const rateText = centsPerSecond.toFixed(2);
      if (rateEl && rateText !== shownRate) {
        rateEl.textContent = rateText;
        shownRate = rateText;
      }
      // Gauge: 0–8 gal/h across the arc's 270°.
      const t = Math.min(gallonsPerHour / 8, 1);
      if (needle) needle.style.transform = `rotate(${-135 + t * 270}deg)`;
      if (arc) arc.style.strokeDashoffset = String(arcLength * (1 - t));
    };

    const tick = (now) => {
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      time += dt;
      // Engine load rises and falls like a city drive.
      const gph = 2.6 + Math.sin(time * 0.55) * 1.1 + Math.sin(time * 1.9) * 0.45 + (Math.sin(time * 0.17) > 0.6 ? 1.6 : 0);
      spent += ((gph * pricePerGallon) / 3600) * dt;
      draw(gph);
      if (running) requestAnimationFrame(tick);
    };

    draw(2.6);
    new IntersectionObserver(([entry]) => {
      const was = running;
      running = entry.isIntersecting;
      if (running && !was) {
        last = performance.now();
        requestAnimationFrame(tick);
      }
    }).observe(el);
  });

  /* Scroll story: which step is showing, and a progress bar. */
  root.querySelectorAll('[data-ga-story]').forEach((story) => {
    const steps = [...story.querySelectorAll('[data-step]')];
    const dots = [...story.querySelectorAll('[data-step-dot]')];
    const bar = story.querySelector('[data-story-bar]');
    let active = -1;
    const update = () => {
      const r = story.getBoundingClientRect();
      const p = Math.min(1, Math.max(0, -r.top / Math.max(r.height - window.innerHeight, 1)));
      const index = Math.min(steps.length - 1, Math.floor(p * steps.length));
      if (bar) bar.style.transform = `scaleX(${p})`;
      if (index !== active) {
        active = index;
        steps.forEach((s, i) => s.classList.toggle('is-active', i === index));
        dots.forEach((d, i) => d.classList.toggle('is-active', i <= index));
      }
    };
    let queued = false;
    window.addEventListener(
      'scroll',
      () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          update();
        });
      },
      { passive: true },
    );
    window.addEventListener('resize', update);
    update();
  });

  /* Parallax: elements drift with --p (0 → 1) as their section crosses the screen. */
  const parallax = [...root.querySelectorAll('[data-progress]')];
  if (parallax.length && !reduce) {
    let queued = false;
    const update = () => {
      queued = false;
      const vh = window.innerHeight;
      parallax.forEach((el) => {
        const r = el.getBoundingClientRect();
        const p = Math.min(1, Math.max(0, (vh - r.top) / (vh + r.height)));
        el.style.setProperty('--p', p.toFixed(3));
      });
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!queued) {
          queued = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true },
    );
    update();
  }

  /* Cards lean toward the mouse, and the hero's glow follows it. */
  if (!reduce && window.matchMedia('(hover: hover)').matches) {
    root.querySelectorAll('[data-tilt]').forEach((card) => {
      card.addEventListener('pointermove', (e) => {
        const r = card.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width;
        const y = (e.clientY - r.top) / r.height;
        card.style.setProperty('--rx', `${(0.5 - y) * 8}deg`);
        card.style.setProperty('--ry', `${(x - 0.5) * 10}deg`);
        card.style.setProperty('--mx', `${x * 100}%`);
        card.style.setProperty('--my', `${y * 100}%`);
      });
      card.addEventListener('pointerleave', () => {
        card.style.setProperty('--rx', '0deg');
        card.style.setProperty('--ry', '0deg');
      });
    });
    const hero = root.querySelector('.ga-hero');
    hero?.addEventListener('pointermove', (e) => {
      const r = hero.getBoundingClientRect();
      hero.style.setProperty('--gx', `${((e.clientX - r.left) / r.width) * 100}%`);
      hero.style.setProperty('--gy', `${((e.clientY - r.top) / r.height) * 100}%`);
    });
  }
})();
